// biome-ignore-all lint/a11y/useSemanticElements: <input type="radio">
//   cannot be styled as a chunky-pill segment with --color-cta-primary
//   background + Fredoka label inside its own bounding box. ARIA APG
//   radiogroup supports composite-widget radios on non-input elements
//   (https://www.w3.org/WAI/ARIA/apg/patterns/radio/). Identical exception
//   to the predecessor SegmentedPills.tsx (Phase 15 Plan 05).
//
// Phase 17 Plan 03 Task 2 — Segmented picker primitive (UI-SPEC §5.1 row 5).
// Phase 18 Plan 02 Task 3 — Added arrow-key navigation + roving tabindex
// (POLISH-09; RESEARCH Pitfall 1).
//
// Radiogroup-shaped composite widget per W3C ARIA APG Radio Group Pattern:
// https://www.w3.org/WAI/ARIA/apg/patterns/radio/
//   - wrapper: role="radiogroup"
//   - each option: <button role="radio" aria-checked={...}>
//   - ArrowLeft/Up moves to previous; ArrowRight/Down moves to next; wrap-around
//   - Roving tabindex: only the selected option is in the tab order
//
// Pieces.css recipe (`.seg button[aria-checked="true"]`) keys selected
// styling off `aria-checked` — the canonical radiogroup attribute. The
// recipe also matches `aria-selected="true"` so non-radiogroup uses of
// `.seg` (e.g. tablist patterns elsewhere) continue to style correctly.

import {
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
  useRef,
} from 'react'

export interface SegOption<T extends string> {
  readonly value: T
  readonly label: string | ReactNode
  readonly ariaLabel?: string
}

export interface SegProps<T extends string> {
  readonly options: ReadonlyArray<SegOption<T>>
  readonly value: T
  readonly onChange: (next: T) => void
  readonly ariaLabel: string
  readonly testId?: string
  readonly style?: CSSProperties
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  testId,
  style,
}: SegProps<T>): JSX.Element {
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([])

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const idx = options.findIndex((o) => o.value === value)
    if (idx === -1) return

    let nextIdx = idx
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIdx = (idx - 1 + options.length) % options.length
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIdx = (idx + 1) % options.length
    } else {
      return
    }
    const nextOpt = options[nextIdx]
    if (!nextOpt) return
    e.preventDefault()
    onChange(nextOpt.value)
    buttonsRef.current[nextIdx]?.focus()
  }

  return (
    <div
      className="seg"
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
      style={style}
      onKeyDown={onKeyDown}
    >
      {options.map((opt, i) => {
        const isSelected = opt.value === value
        return (
          <button
            key={opt.value}
            ref={(el) => {
              buttonsRef.current[i] = el
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={opt.ariaLabel}
            tabIndex={isSelected ? 0 : -1}
            data-pill-value={opt.value}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
